<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">
        {{ $t('materialClearanceByProject.pageTitle') }}
      </h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          { text: $t('materialClearanceByProject.pageTitle'), active: true }
        ]"
      />
    </div>
    <ts-loading-banner :loading="loading">
      <ts-card>
        <ts-card-wrapper>
          <div class="row">
            <div class="col-md-3">
              <label>{{ $t('materialClearanceByProject.project') }}</label>
              <a-select
                v-model="model.project_id"
                :options="projects"
                showSearch
                :filterOption="filterOption"
                placeholder="--select--"
                mode="multiple"
                class="ts-w-full"
                :max-tag-count="1"
                :maxTagTextLength="30"
                @change="fetchProjectRelatedData"
              />
              <ts-checkbox v-model="model.clearance" class="ts-mt-2">
                {{ $t('materialClearanceByProject.clearance') }}
              </ts-checkbox>
              <div class="validate" v-if="errors.has('project_id')">
                {{ errors.first('project_id') }}
              </div>
            </div>
            <div class="col-md-3">
              <label>{{
                $t('materialClearanceByProject.borrowingNumber')
              }}</label>
              <a-select
                v-model="model.sfo_id"
                :options="borrowing"
                showSearch
                :filterOption="filterOption"
                placeholder="--select--"
                mode="multiple"
                class="ts-w-full"
                :max-tag-count="1"
                :maxTagTextLength="30"
                @change="fetchOwnerBySfo"
              />
            </div>
            <div class="col-md-3">
              <label>{{ $t('materialClearanceByProject.requestBy') }}</label>
              <a-select
                v-model="model.employee_id"
                :options="ownerByProjects"
                showSearch
                :filterOption="filterOption"
                placeholder="--select--"
                mode="multiple"
                class="ts-w-full"
                :max-tag-count="1"
                :maxTagTextLength="30"
              />
            </div>
          </div>
          <div class="row ts-mt-2">
            <div class="col-md-9 ts-flex ts-justify-end ts-space-x-2 ts-h-8">
              <ts-button @click="onPreview" color="primary">{{
                $t('view')
              }}</ts-button>
              <ts-button @click="exportExcel" color="info">{{
                $t('excel')
              }}</ts-button>
              <ts-button @click="previewPdf" color="info">{{
                $t('preview')
              }}</ts-button>
            </div>
          </div>
        </ts-card-wrapper>
      </ts-card>

      <ts-card>
        <ts-card-wrapper>
          <ts-table :columns="columns" :records="resources">
            <template v-slot="{ record, index }">
              <td class="white-space-nowrap">{{ index + 1 }}</td>
              <td class="white-space-nowrap">{{ record.barcode }}</td>
              <td class="white-space-nowrap">{{ record.product_name_en }}</td>
              <td class="white-space-nowrap">{{ record.location_name_en }}</td>
              <td class="ts-text-center">{{ record.quantity_order }}</td>
              <td class="ts-text-center">{{ record.used_qty }}</td>
              <td class="ts-text-center">{{ record.quantity_return }}</td>
              <td class="ts-text-center">{{ record.lost_qty }}</td>
              <td class="white-space-nowrap">{{ record.customer_name }}</td>
              <td class="whitespace-no-wrap">
                {{ record.remarks }}
              </td>
            </template>
          </ts-table>
        </ts-card-wrapper>
      </ts-card>
    </ts-loading-banner>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { mapActions } from 'vuex'
import { trim } from 'lodash'
import * as FileDownload from 'downloadjs'
import MaterialPreviewPdf from './components/material-preview-pdf.vue'

export default {
  name: 'materialClearanceByProject',
  data() {
    return {
      errors: new Errors(),
      loading: false,
      projects: [],
      resources: [],
      ownerByProjects: [],
      borrowing: [],
      model: {
        project_id: undefined,
        sfo_id: undefined,
        employee_id: undefined,
        clearance: true
      }
    }
  },
  mounted() {
    this.fetchData()
  },
  computed: {
    columns() {
      return [
        { name: this.$t('materialClearanceByProject.numbering') },
        { name: this.$t('materialClearanceByProject.barcode') },
        { name: this.$t('materialClearanceByProject.productName') },
        { name: this.$t('materialClearanceByProject.warehouseName') },
        { name: this.$t('materialClearanceByProject.borrowQty') },
        { name: this.$t('materialClearanceByProject.usedQty') },
        { name: this.$t('materialClearanceByProject.returnQty') },
        { name: this.$t('materialClearanceByProject.lostQty') },
        { name: this.$t('materialClearanceByProject.customerName') },
        { name: this.$t('materialClearanceByProject.remark') }
      ]
    }
  },
  methods: {
    ...mapActions('report/project', [
      'getProject',
      'getOwnerByProject',
      'getBorrowing',
      'materialClearanceByProject',
      'materialClearanceByProjectExcel'
    ]),
    async fetchData() {
      this.loading = true
      try {
        // Fetch all initial data without filters
        const [projects, owners, borrowing] = await Promise.all([
          this.getProject().catch(() => []),
          this.getOwnerByProject().catch(() => []),
          this.getBorrowing().catch(() => [])
        ])

        this.projects = Array.isArray(projects)
          ? projects.map(el => ({
              value: el.project_id,
              label: el.project_name
            }))
          : []

        this.ownerByProjects = Array.isArray(owners)
          ? owners.map(el => ({
              value: el.employee_id,
              label: el.employee_name_en
            }))
          : []

        this.borrowing = Array.isArray(borrowing)
          ? borrowing.map(el => ({
              value: el.sfo_id,
              label: el.sfo_number
            }))
          : []
      } catch (error) {
        console.error('Error fetching data:', error)
        this.$toasted.error('Failed to load data')
        this.projects = []
        this.ownerByProjects = []
        this.borrowing = []
      } finally {
        this.loading = false
      }
    },
    async fetchProjectRelatedData() {
      // If no projects selected, reload all data
      if (!this.model.project_id || this.model.project_id.length === 0) {
        await this.fetchData()
        return
      }

      this.loading = true

      try {
        // Fetch borrowing and owners filtered by selected projects
        const [borrowing, owners] = await Promise.all([
          this.getBorrowing({
            project_id: this.model.project_id
          }).catch(() => []),
          this.getOwnerByProject({
            project_id: this.model.project_id
          }).catch(() => [])
        ])

        this.borrowing = Array.isArray(borrowing)
          ? borrowing.map(el => ({
              value: el.sfo_id,
              label: el.sfo_number
            }))
          : []

        this.ownerByProjects = Array.isArray(owners)
          ? owners.map(el => ({
              value: el.employee_id,
              label: el.employee_name_en
            }))
          : []

        // Remove selected values that are no longer in the filtered list
        if (this.model.sfo_id) {
          const validSfoIds = this.borrowing.map(b => b.value)
          if (Array.isArray(this.model.sfo_id)) {
            this.model.sfo_id = this.model.sfo_id.filter(id =>
              validSfoIds.includes(id)
            )
            if (this.model.sfo_id.length === 0) {
              this.model.sfo_id = undefined
            }
          } else {
            if (!validSfoIds.includes(this.model.sfo_id)) {
              this.model.sfo_id = undefined
            }
          }
        }

        if (this.model.employee_id) {
          const validEmployeeIds = this.ownerByProjects.map(o => o.value)
          if (Array.isArray(this.model.employee_id)) {
            this.model.employee_id = this.model.employee_id.filter(id =>
              validEmployeeIds.includes(id)
            )
            if (this.model.employee_id.length === 0) {
              this.model.employee_id = undefined
            }
          } else {
            if (!validEmployeeIds.includes(this.model.employee_id)) {
              this.model.employee_id = undefined
            }
          }
        }
      } catch (error) {
        console.error('Error fetching project data:', error)
        this.$toasted.error('Failed to load project data')
        this.borrowing = []
        this.ownerByProjects = []
      } finally {
        this.loading = false
      }
    },
    async fetchOwnerBySfo() {
      // If no SFO selected, fallback to project filter
      if (!this.model.sfo_id || this.model.sfo_id.length === 0) {
        await this.fetchProjectRelatedData()
        return
      }

      this.loading = true
      try {
        const owners = await this.getOwnerByProject({
          sfo_id: this.model.sfo_id
        }).catch(() => [])

        this.ownerByProjects = Array.isArray(owners)
          ? owners.map(el => ({
              value: el.employee_id,
              label: el.employee_name_en
            }))
          : []

        // Remove invalid employee selections
        if (this.model.employee_id) {
          const validEmployeeIds = this.ownerByProjects.map(o => o.value)
          if (Array.isArray(this.model.employee_id)) {
            this.model.employee_id = this.model.employee_id.filter(id =>
              validEmployeeIds.includes(id)
            )
            if (this.model.employee_id.length === 0) {
              this.model.employee_id = undefined
            }
          } else {
            if (!validEmployeeIds.includes(this.model.employee_id)) {
              this.model.employee_id = undefined
            }
          }
        }
      } catch (error) {
        console.error('Error fetching owner by SFO:', error)
        this.$toasted.error('Failed to load owners by borrowing')
        this.ownerByProjects = []
      } finally {
        this.loading = false
      }
    },

    onPreview() {
      this.loading = true
      this.materialClearanceByProject(this.model)
        .then(result => {
          this.resources = result.data
        })
        .catch(err => {
          this.$toasted.error(err.message)
          this.errors = new Errors(err.errors)
        })
        .finally(() => (this.loading = false))
    },
    exportExcel() {
      this.loading = true
      this.errors = new Errors()
      this.materialClearanceByProjectExcel(this.model)
        .then(response => {
          let filename = trim(
            response.headers['content-disposition']
              .substring(
                response.headers['content-disposition'].indexOf('filename')
              )
              .replace('filename=', ''),
            '"'
          )
          FileDownload(response.data, filename)
        })
        .catch(err => {
          let error = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(err))
          )
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => (this.loading = false))
    },
    filterOption(input, option) {
      return option.componentOptions.children[0].text
        .toLowerCase()
        .includes(input.toLowerCase())
    },
    previewPdf() {
      this.$modal.show(
        MaterialPreviewPdf,
        {
          project_id: this.model.project_id || [],
          sfo_id: this.model.sfo_id || [],
          employee_id: this.model.employee_id || [],
          clearance: this.model.clearance
        },
        {
          height: 'auto',
          width: '70%',
          scrollable: true,
          draggable: '.form-header',
          clickToClose: false
        }
      )
    }
  }
}
</script>

<style scoped>
.white-space-nowrap {
  white-space: nowrap;
}
</style>
